import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { AlertController, IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { PtiService } from '../../../providers/pti/pti.service';
import { UserService } from '../../../providers/auth/user.service';
import { TranslateService } from '@ngx-translate/core';
import { CountdownTimerComponent } from '../../countdown-timer/countdown-timer.component';
import { GeoLocalisationProvider } from '../../../providers/geolocalisation/geolocation.provider';
import { Geolocation, Position, PermissionStatus } from '@capacitor/geolocation';
import { LoggerWrapper } from '../../../providers/logger/logger-wrapper.service';
import { LoggerFactory } from '../../../providers/logger/logger-factory.service';

@Component({
  selector: 'sos-modal',
  templateUrl: './sos.modal.html',
  styleUrl: './sos.modal.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, CountdownTimerComponent],
})
export class SosModal {
  private readonly modalController: ModalController = inject(ModalController);
  private readonly ptiService: PtiService = inject(PtiService);
  private readonly userService: UserService = inject(UserService);
  private readonly alertCtrl: AlertController = inject(AlertController);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly loadingCtrl: LoadingController = inject(LoadingController);
  private readonly geoLocalisationProvider: GeoLocalisationProvider = inject(GeoLocalisationProvider);
  private readonly loggerFactory: LoggerFactory = inject(LoggerFactory);

  @ViewChild('timer', { static: true }) timer: CountdownTimerComponent;

  private logger: LoggerWrapper;

  readonly disableButton = signal<boolean>(false);

  constructor() {
    this.logger = this.loggerFactory.getInstance(this, true);
  }

  async stopAndCloseModal() {
    this.timer.stop();
    await this.modalController.dismiss();
  }

  async onCountdownDone() {
    await this.sendSOS();
  }

  async sendSOS() {
    const loading = await this.loadingCtrl.create();
    await loading.present();
    try {
      this.stop();
      const dkCodeRessource = this.userService.getCurrentUser().dkCodeRessource;
      const response = await this.ptiService.createPTI(dkCodeRessource);
      await this.sendPositionGps();

      if (response?.numeroDemande) {
        await this.showAlert(null, this.translate.instant('PTI.MESSAGES.SUCCESS'), () => this.stopAndCloseModal());
      }
    } catch (e) {
      await this.showAlert('ERREUR', this.translate.instant('PTI.MESSAGES.FAIL'), () => this.resume());
    } finally {
      await loading.dismiss();
    }
  }

  private async sendPositionGps() {
    let permissionStatus: PermissionStatus = await Geolocation.checkPermissions();
    if (permissionStatus.location === 'granted') {
      try {
        const positionGpsTelephone: Position = await this.geoLocalisationProvider.getCurrentPosition();

        const latitude: number = positionGpsTelephone.coords.latitude;
        const longitude: number = positionGpsTelephone.coords.longitude;
        await this.geoLocalisationProvider.pushGeoPosition(false, latitude.toString(), longitude.toString());
        await this.geoLocalisationProvider.pushGeoPosition(
          false,
          (latitude + 0.00001).toString(),
          (longitude + 0.00001).toString()
        );
      } catch (error) {
        this.logger.error(error, 'Erreur lors de la récupération de la position GPS');
      }
    }
  }

  private stop() {
    this.disableButton.set(true);
    this.timer.stop();
  }

  private resume() {
    this.disableButton.set(false);
    this.timer.resume();
  }

  private async showAlert(header: string = null, message: string, handler: () => void) {
    const alert = await this.alertCtrl.create({
      header: header,
      message: message,
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('GLOBAL.OK'),
          handler: handler,
        },
      ],
    });
    await alert.present();
  }
}
